import React, { ComponentProps, useMemo, useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import SvgIcon from '@mui/material/SvgIcon'
import { Fade } from '@mui/material'
import Tabs from '../../shared/components/Tabs/Tabs'
import TppImpactTab from './tabs/TppImpactTab/TppImpactTab'
import ImpactMetricsTab from './tabs/ImpactMetricsTab/ImpactMetricsTab'
import ActivityMetricsTab from './tabs/ActivityMetricsTab/ActivityMetricsTab'
import { ReactComponent as MetricsIcon } from '../../assets/images/science-graph.svg'
import { useIsOtsukaOrganization } from '../../shared/hooks/useIsOtsukaOrganization'
import useActivitiesDisplayName from '../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'

import Highcharts from 'highcharts'
import highchartsGantt from 'highcharts/modules/gantt'
import { ArcElement, Chart, Legend, Title, Tooltip } from 'chart.js'

Chart.register(ArcElement, Tooltip, Legend, Title)

highchartsGantt(Highcharts)

type MetricsViewTabId = 'tpp' | 'value-message' | 'activity'

const MetricsView = ({
    selectedTabId: controlledSelectedTabId,
    selectedScenarioSlug,
    onTabChange,
    onScenarioChange,
}: {
    selectedTabId?: MetricsViewTabId
    selectedScenarioSlug?: string | null
    onTabChange?: (tabId: MetricsViewTabId) => void
    onScenarioChange?: (scenarioSlug: string) => void
} = {}) => {
    const [uncontrolledSelectedTabId, setUncontrolledSelectedTabId] = useState<MetricsViewTabId>('value-message')
    const selectedTabId = controlledSelectedTabId ?? uncontrolledSelectedTabId
    const isOtsuka = useIsOtsukaOrganization()
    const activitiesDisplayName = useActivitiesDisplayName()
    const tabs = useMemo(
        (): ComponentProps<typeof Tabs>['tabs'] => [
            { id: 'value-message', label: 'Messaging Metrics', view: <ImpactMetricsTab /> },
            { id: 'activity', label: `${activitiesDisplayName.plural} Metrics`, view: <ActivityMetricsTab /> },
            {
                id: 'tpp',
                label: isOtsuka ? 'TrPP Metrics' : 'TPP Metrics',
                view: <TppImpactTab selectedScenarioSlug={selectedScenarioSlug} onScenarioChange={onScenarioChange} />,
            },
        ],
        [isOtsuka, activitiesDisplayName.plural, selectedScenarioSlug, onScenarioChange]
    )

    return (
        <Fade in timeout={800}>
            <Stack flex={1} direction="column">
                <Stack direction="row" color="figma.grayscale.textHier1" spacing={1.5} alignItems="center" p={5}>
                    <SvgIcon component={MetricsIcon} inheritViewBox sx={{ fontSize: '32px' }} />
                    <Typography variant="28x600">Metrics</Typography>
                </Stack>

                <Tabs
                    orientation="horizontal"
                    tabs={tabs}
                    selectedTabId={selectedTabId}
                    onTabChange={(tabId) => {
                        const nextTabId = tabId as MetricsViewTabId

                        if (controlledSelectedTabId === undefined) {
                            setUncontrolledSelectedTabId(nextTabId)
                        }

                        onTabChange?.(nextTabId)
                    }}
                    tabPanelSx={{ px: 2, py: 3 }}
                />
            </Stack>
        </Fade>
    )
}

export default MetricsView
